import { computed } from "vue";
import { getStore } from "@/store";
import { useMessage } from "naive-ui";
import { useApolloClient } from "@vue/apollo-composable";
import { ApolloClient, InMemoryCache } from "@apollo/client/core";
import { getAllServiceGroups } from "@/api/serviceGroups/getAllServiceGroups";
import { getMyServiceGroups } from "@/api/serviceGroups/getMyServiceGroups";
import type { MessageApi } from "naive-ui";

const isAdmin = computed(() => getStore().getters["user/isAdmin"]);

/**
 * Load and set the service groups for users, calling the correct API
 * based on whether they are an admin or not.
 *
 * NOTE: This implementation may change in the future, with all servicegroups,
 * for both admins and users, being able to be pulled from a single
 * endpoint.
 * @param client - apollo client
 * @param message - the naive ui message api.
 */
async function setUserServiceGroups(
  client: ApolloClient<InMemoryCache>,
  message: MessageApi
) {
  const store = getStore();
  let serviceGroups;

  if (isAdmin.value) {
    const response = await getAllServiceGroups(client);

    if (response.failure) {
      message.error(
        "Unable to access services groups. Please reach out to support@telemetryhub.com for assistance."
      );

      return;
    }

    serviceGroups = response.appGroups?.appGroups;
  } else {
    const response = await getMyServiceGroups(client);

    if (response.failure) {
      message.error(
        "Unable to access services groups. Please reach out to support@telemetryhub.com for assistance."
      );

      return;
    }
    serviceGroups = response.appGroups?.appGroups.map((ag) => ag.appGroup);
  }

  store.commit("user/setServiceGroups", {
    serviceGroups: serviceGroups,
  });
}

/**
 * Retrieve apollo client and provide setUserServiceGroups
 * function to get the users accessible appGroups.
 * @returns setUserServiceGroups composition functionality.
 */
export function useSetUserServiceGroups(): {
  setUserServiceGroups: () => Promise<void>;
} {
  const { resolveClient } = useApolloClient();
  const message = useMessage();
  return {
    setUserServiceGroups: () => setUserServiceGroups(resolveClient(), message),
  };
}
